import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, Save, Trash2, Loader2, PenLine, Mail, Send, MessageSquare, Link as LinkIcon, Unlink } from "lucide-react";
import api from "@/lib/api";

const inp = "w-full bg-muted/40 border border-border rounded-lg px-4 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50";

export default function Profile() {
  const [me, setMe] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const drawState = useRef({ down: false, last: null });
  const [o365, setO365] = useState({ tenant_id: "", client_id: "", client_secret: "", sender_email: "", enabled: false });
  const [orgO365, setOrgO365] = useState(null); // {enabled, has_credentials, reply_to_email}
  const [o365Saving, setO365Saving] = useState(false);
  const [o365Testing, setO365Testing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [cuToken, setCuToken] = useState("");
  const [cuBusy, setCuBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data));
    api.get("/office365/me").then((r) => setO365({ ...o365, ...(r.data || {}) })).catch(() => {});
    // Fetch org O365 status (public, only exposes booleans + reply-to)
    api.get("/office365/org-status").then((r) => setOrgO365(r.data)).catch(() => setOrgO365({ has_credentials: false }));
  // eslint-disable-next-line
  }, []);

  const saveO365 = async () => {
    setO365Saving(true);
    try {
      const { data } = await api.put("/office365/me", o365);
      setO365({ ...o365, ...data });
      toast.success("Configuração Office 365 guardada");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); } finally { setO365Saving(false); }
  };

  const testO365 = async () => {
    setO365Testing(true);
    try {
      await api.post("/office365/me/test");
      toast.success("Email de teste enviado para o próprio remetente");
    } catch (e) { toast.error(e.response?.data?.detail || "Falhou"); } finally { setO365Testing(false); }
  };

  const pick = () => fileRef.current?.click();

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Escolha uma imagem"); return; }
    if (f.size > 2_000_000) { toast.error("Imagem demasiado grande (máx 2MB)"); return; }
    const r = new FileReader();
    r.onload = () => setMe((m) => ({ ...m, signature_data_url: r.result }));
    r.readAsDataURL(f);
  };

  const startDraw = () => {
    setDrawing(true);
    setTimeout(() => {
      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#F8FAFC";
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.strokeStyle = "#0F172A";
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
    }, 50);
  };

  const canvasPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - rect.top;
    return { x: x * (c.width / rect.width), y: y * (c.height / rect.height) };
  };

  const onCanvasDown = (e) => { e.preventDefault(); drawState.current.down = true; drawState.current.last = canvasPos(e); };
  const onCanvasMove = (e) => {
    if (!drawState.current.down) return;
    e.preventDefault();
    const p = canvasPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(drawState.current.last.x, drawState.current.last.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    drawState.current.last = p;
  };
  const onCanvasUp = () => { drawState.current.down = false; };

  const useDrawn = () => {
    const url = canvasRef.current.toDataURL("image/png");
    setMe((m) => ({ ...m, signature_data_url: url }));
    setDrawing(false);
    toast.success("Assinatura pronta. Não esqueça de guardar.");
  };

  const clearCanvas = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#F8FAFC";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#0F172A";
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me", {
        name: me.name, title: me.title, phone: me.phone,
        bio: me.bio, signature_data_url: me.signature_data_url,
      });
      setMe(data);
      // update cached user
      const raw = localStorage.getItem("caring_user");
      if (raw) { const u = JSON.parse(raw); localStorage.setItem("caring_user", JSON.stringify({ ...u, name: data.name })); }
      toast.success("Perfil atualizado");
    } catch(e){ toast.error(e.response?.data?.detail || "Erro"); } finally { setSaving(false); }
  };

  const removeSig = async () => {
    if (!window.confirm("Remover assinatura?")) return;
    await api.delete("/auth/me/signature");
    setMe((m) => ({ ...m, signature_data_url: null }));
    toast.success("Assinatura removida");
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Ficheiro > 5MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/auth/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setMe((m) => ({ ...m, photo_url: data.photo_url }));
      toast.success("Foto atualizada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro no upload");
    }
    e.target.value = "";
  };

  const removePhoto = async () => {
    if (!window.confirm("Remover foto?")) return;
    await api.delete("/auth/me/photo");
    setMe((m) => ({ ...m, photo_url: null }));
    toast.success("Foto removida");
  };

  const connectClickUp = async () => {
    const t = (cuToken || "").trim();
    if (!t) { toast.error("Cole o seu ClickUp Personal API Token"); return; }
    setCuBusy(true);
    try {
      const { data } = await api.post("/auth/me/clickup-token", { token: t });
      setMe((m) => ({ ...m, clickup_connected: true, clickup_user_id: data.clickup_user_id || m?.clickup_user_id }));
      setCuToken("");
      toast.success(`ClickUp ligado como ${data.clickup_username || "utilizador"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível ligar o ClickUp");
    } finally {
      setCuBusy(false);
    }
  };

  const disconnectClickUp = async () => {
    if (!window.confirm("Desligar o seu ClickUp? As RTs voltam a ser publicadas em nome do admin.")) return;
    setCuBusy(true);
    try {
      await api.delete("/auth/me/clickup-token");
      setMe((m) => ({ ...m, clickup_connected: false }));
      toast.success("ClickUp desligado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally {
      setCuBusy(false);
    }
  };

  if (!me) return <div className="p-10 text-muted-foreground">A carregar...</div>;

  return (
    <div className="p-8 lg:p-12 max-w-3xl mx-auto space-y-6" data-testid="profile-root">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">A minha conta</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight">Perfil</h1>
        <p className="text-muted-foreground text-sm mt-2">A sua assinatura aparecerá automaticamente na última página das propostas em que estiver atribuído como Gestor de Cliente.</p>
      </div>

      <div className="bg-card border border-border rounded-2xl p-8 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Nome</div>
            <input data-testid="prof-name" className={inp} value={me.name||""} onChange={(e)=>setMe({...me, name:e.target.value})} />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Cargo</div>
            <input data-testid="prof-title" className={inp} placeholder="Ex: Gestora de Cliente Sénior" value={me.title||""} onChange={(e)=>setMe({...me, title:e.target.value})} />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Email</div>
            <input className={inp + " opacity-60"} value={me.email||""} disabled />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Telefone</div>
            <input data-testid="prof-phone" className={inp} placeholder="+351 ..." value={me.phone||""} onChange={(e)=>setMe({...me, phone:e.target.value})} />
          </label>
          <label className="md:col-span-2 block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">
              Bio para a proposta ao cliente
              <span className="ml-2 normal-case text-muted-foreground/70 tracking-normal">(2-4 frases; substitui o texto padrão)</span>
            </div>
            <textarea data-testid="prof-bio" rows={4} className={inp + " resize-y"} placeholder="Ex: Sou a Ana Ribeiro, Gestora Sénior de Clientes na Caring há 12 anos…"
              value={me.bio||""} onChange={(e)=>setMe({...me, bio:e.target.value})} maxLength={800} />
            <div className="text-[10px] text-muted-foreground/70 mt-1 text-right">{(me.bio||"").length}/800</div>
          </label>
        </div>
      </div>

      {/* Manager photo card */}
      <div className="bg-card border border-border rounded-2xl p-8 space-y-5" data-testid="prof-photo-card">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-primary" />
          <h3 className="font-ui text-lg">Foto de perfil</h3>
          <span className="ml-2 text-xs text-muted-foreground">— visível ao cliente na Proposta Final</span>
        </div>
        <div className="flex items-center gap-6 flex-wrap">
          <div className="w-32 h-32 rounded-full border-2 border-primary bg-[hsl(var(--info-bg))] overflow-hidden flex items-center justify-center flex-shrink-0" data-testid="prof-photo-preview">
            {me.photo_url ? (
              <img src={me.photo_url} alt="Foto" className="w-full h-full object-cover" />
            ) : (
              <span className="font-display text-5xl text-primary">
                {(me.name || "?").trim().charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-[200px] space-y-2">
            <p className="text-sm text-muted-foreground">
              JPG, PNG ou WebP · até 5 MB. Recomendado quadrado, mínimo 400×400px.
            </p>
            <div className="flex gap-2 flex-wrap">
              <label className="px-4 py-2 rounded-lg bg-primary hover:bg-primary text-primary-foreground text-sm font-semibold cursor-pointer inline-flex items-center gap-2" data-testid="prof-photo-upload-label">
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadPhoto} data-testid="prof-photo-upload-input" />
                {me.photo_url ? "Substituir foto" : "Carregar foto"}
              </label>
              {me.photo_url && (
                <button onClick={removePhoto} className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:bg-background" data-testid="prof-photo-remove">
                  Remover
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-8 space-y-5">
        <div className="flex items-center gap-2"><PenLine className="w-4 h-4 text-primary" /><h3 className="font-ui text-lg">Assinatura</h3></div>

        <div className="bg-muted/40 border border-border rounded-xl p-6 flex items-center justify-center min-h-[140px]">
          {me.signature_data_url ? (
            <img src={me.signature_data_url} alt="Assinatura" className="max-h-24 max-w-full object-contain" data-testid="signature-preview" />
          ) : (
            <div className="text-sm text-muted-foreground/70">Sem assinatura</div>
          )}
        </div>

        {drawing && (
          <div className="space-y-3" data-testid="signature-draw-area">
            <canvas
              ref={canvasRef}
              width={720}
              height={200}
              className="w-full rounded-xl border border-primary/40 touch-none cursor-crosshair"
              onMouseDown={onCanvasDown} onMouseMove={onCanvasMove} onMouseUp={onCanvasUp} onMouseLeave={onCanvasUp}
              onTouchStart={onCanvasDown} onTouchMove={onCanvasMove} onTouchEnd={onCanvasUp}
            />
            <div className="flex gap-2">
              <button data-testid="sig-clear" onClick={clearCanvas} className="text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5">Limpar</button>
              <button data-testid="sig-use" onClick={useDrawn} className="text-xs bg-foreground hover:bg-slate-800 text-background font-semibold rounded-full px-4 py-1.5">Usar esta assinatura</button>
              <button onClick={()=>setDrawing(false)} className="text-xs text-muted-foreground hover:text-foreground ml-auto">Cancelar</button>
            </div>
          </div>
        )}

        {!drawing && (
          <div className="flex flex-wrap gap-3">
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="sig-file" />
            <button data-testid="sig-upload" onClick={pick} className="inline-flex items-center gap-2 bg-muted hover:bg-muted border border-border rounded-full px-4 py-2 text-sm">
              <Upload className="w-4 h-4" /> Carregar imagem
            </button>
            <button data-testid="sig-draw" onClick={startDraw} className="inline-flex items-center gap-2 bg-muted hover:bg-muted border border-border rounded-full px-4 py-2 text-sm">
              <PenLine className="w-4 h-4" /> Desenhar
            </button>
            {me.signature_data_url && (
              <button data-testid="sig-remove" onClick={removeSig} className="inline-flex items-center gap-2 border border-rose-500/30 text-rose-300 hover:bg-rose-500/10 rounded-full px-4 py-2 text-sm">
                <Trash2 className="w-4 h-4" /> Remover
              </button>
            )}
          </div>
        )}
        <div className="text-xs text-muted-foreground">PNG, JPG, SVG · máx. 2MB · fundo transparente ou escuro fica melhor.</div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-8 space-y-4" data-testid="email-signature-card">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-foreground" />
          <h3 className="font-ui text-lg">Assinatura para emails</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          É adicionada automaticamente ao fim de cada email enviado por si. Combina a tua <b>assinatura desenhada à mão</b> (card acima) + o número verde da empresa <b>800 450 191</b> + o site <b>caring.pt</b>.
        </p>
        <div className="bg-muted/40 border border-primary/40 rounded-lg p-4" data-testid="email-signature-preview">
          {me.signature_data_url ? (
            <img src={me.signature_data_url} alt="assinatura desenhada" style={{maxHeight: 70, maxWidth: 260, display: "block", marginBottom: 4}} />
          ) : (
            <div className="text-xs text-[hsl(var(--danger))] italic mb-2">⚠ Ainda não tens assinatura desenhada. Desenha-a no card acima para aparecer nos emails.</div>
          )}
          <div style={{fontFamily: "'Playfair Display', 'Book Antiqua', Georgia, serif", fontWeight: 600, color: "#0F172A", fontSize: 18, letterSpacing: "0.01em"}}>{me.name || ""}</div>
          {me.title && <div style={{color: "#6B7280", fontSize: 12, fontStyle: "italic"}}>{me.title}</div>}
          <div style={{marginTop: 8, color: "#6B7280", fontSize: 12}}>
            📞 Número verde <b>800 450 191</b> · 🌐 <a href="https://caring.pt" style={{color: "#1D4ED8", textDecoration: "none"}}>caring.pt</a>
          </div>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-8 space-y-5" data-testid="o365-card">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-foreground" />
          <h3 className="font-ui text-lg">O meu Office 365</h3>
          <span className="ml-auto text-[10px] uppercase tracking-[0.15em] text-muted-foreground/70">Envio de emails por mim</span>
        </div>

        {/* Estado da configuração da organização */}
        {orgO365 && (
          orgO365.has_credentials ? (
            <div className="rounded-xl border border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] px-4 py-3 text-sm text-[#059669] flex items-start gap-2" data-testid="o365-org-ok">
              <span className="text-[#059669] font-semibold">✓</span>
              <div>
                As credenciais Azure/Office 365 da <b>organização</b> já estão configuradas. Só precisas de indicar o teu email e ativar.
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] px-4 py-3 text-sm text-[hsl(var(--warning))] flex items-start gap-2" data-testid="o365-org-missing">
              <span>⚠</span>
              <div>
                As credenciais Azure ainda não estão configuradas ao nível da organização. Pede a um administrador para as configurar em <b>Office 365</b> ou expande o modo avançado abaixo para introduzir credenciais próprias.
              </div>
            </div>
          )
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <label className="block md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">O meu email remetente *</div>
            <input data-testid="o365-me-sender" type="email" placeholder="nome@caring.pt" className={inp} value={o365.sender_email||""} onChange={(e)=>setO365({...o365, sender_email:e.target.value})} />
          </label>
        </div>

        <label className="flex items-center gap-3">
          <input data-testid="o365-me-enabled" type="checkbox" checked={!!o365.enabled} onChange={(e)=>setO365({...o365, enabled:e.target.checked})} className="accent-[#1D4ED8]" />
          <span className="text-sm text-muted-foreground">Ativar envio a partir da minha conta</span>
        </label>

        {/* Modo avançado (colapsado por defeito) */}
        <div className="border-t border-border pt-4">
          <button
            type="button"
            onClick={()=>setShowAdvanced(v=>!v)}
            className="text-xs text-foreground hover:underline font-semibold inline-flex items-center gap-1"
            data-testid="o365-toggle-advanced"
          >
            {showAdvanced ? "▾" : "▸"} Modo avançado (credenciais Azure próprias)
          </button>
          {showAdvanced && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-testid="o365-advanced">
              <p className="md:col-span-2 text-xs text-muted-foreground">
                Só necessário se pretendes usar uma Aplicação Azure diferente da configurada para a organização. Deixa vazio para herdar as credenciais da organização.
              </p>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Tenant ID</div>
                <input data-testid="o365-me-tenant" className={inp} value={o365.tenant_id||""} onChange={(e)=>setO365({...o365, tenant_id:e.target.value})} />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Client ID</div>
                <input data-testid="o365-me-client" className={inp} value={o365.client_id||""} onChange={(e)=>setO365({...o365, client_id:e.target.value})} />
              </label>
              <label className="block md:col-span-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Client Secret</div>
                <input data-testid="o365-me-secret" type="password" placeholder="••••••••" className={inp} value={o365.client_secret||""} onChange={(e)=>setO365({...o365, client_secret:e.target.value})} />
              </label>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          <button data-testid="o365-me-save" onClick={saveO365} disabled={o365Saving} className="inline-flex items-center gap-2 bg-foreground hover:bg-slate-800 text-background font-semibold rounded-md px-4 py-2 text-sm disabled:opacity-60">
            {o365Saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
          </button>
          <button data-testid="o365-me-test" onClick={testO365} disabled={o365Testing || !o365.enabled} className="inline-flex items-center gap-2 bg-muted hover:bg-muted border border-border text-muted-foreground rounded-md px-4 py-2 text-sm disabled:opacity-40">
            {o365Testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar teste
          </button>
        </div>
      </div>

      {/* --- ClickUp personal token --- */}
      <div className="bg-card border border-border rounded-2xl p-8 space-y-5" data-testid="prof-clickup-card">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-primary" />
          <h3 className="font-ui text-lg">ClickUp — RTs em nome do próprio</h3>
          {me.clickup_connected ? (
            <span className="ml-auto shift-chip shift-chip-sage" data-testid="clickup-status-connected">Ligado</span>
          ) : (
            <span className="ml-auto shift-chip shift-chip-danger" data-testid="clickup-status-disconnected">Não ligado</span>
          )}
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Cole aqui o seu <b>Personal API Token</b> do ClickUp para que o resumo da RT publicado no canal
          "Reuniões Técnicas · Lead" apareça como enviado por si (e não pelo admin que ligou a integração).
        </p>
        <p className="text-xs text-muted-foreground">
          Como obter: no ClickUp abra <b>Settings → Apps → API Token</b> e clique em <b>Generate</b> (começa com <code>pk_</code>).
        </p>

        {me.clickup_connected ? (
          <div className="flex flex-wrap gap-3">
            <button
              onClick={disconnectClickUp}
              disabled={cuBusy}
              data-testid="clickup-disconnect"
              className="inline-flex items-center gap-2 bg-muted hover:bg-muted border border-border text-foreground rounded-md px-4 py-2 text-sm disabled:opacity-60"
            >
              {cuBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />} Desligar ClickUp
            </button>
            <div className="text-xs text-muted-foreground self-center">
              O token está guardado. Para trocar, desligue primeiro e volte a ligar com o novo token.
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3 items-end">
            <label className="flex-1 min-w-[280px]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Personal API Token</div>
              <input
                data-testid="clickup-token-input"
                type="password"
                placeholder="pk_XXXXXXXXXX"
                className={inp}
                value={cuToken}
                onChange={(e) => setCuToken(e.target.value)}
                autoComplete="off"
              />
            </label>
            <button
              onClick={connectClickUp}
              disabled={cuBusy || !cuToken.trim()}
              data-testid="clickup-connect"
              className="inline-flex items-center gap-2 bg-foreground hover:bg-slate-800 text-background font-semibold rounded-md px-4 py-2 text-sm disabled:opacity-60"
            >
              {cuBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />} Ligar ClickUp
            </button>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button data-testid="prof-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-foreground hover:bg-slate-800 text-background font-semibold rounded-full px-6 py-3 text-sm">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar
        </button>
      </div>
    </div>
  );
}
